/**
 * FilterUtils - Utility functions for collection filtering
 * Consolidates common patterns used across collection filtering components
 */

export class FilterUtils {
  /**
   * Keep two sliders in sync - when one changes, update the other
   * Used for mobile/desktop price slider synchronization
   */
  static synchronizeSliders(slider1, slider2, callback = null) {
    if (!slider1 || !slider2) return;

    let isUpdating = false;

    // Sync slider1 to slider2
    slider1.on('update', (values, handle) => {
      if (isUpdating) return;
      isUpdating = true;
      
      try {
        slider2.set(values);
        if (callback) callback(values, handle);
      } catch (error) {
        console.warn('Error syncing slider1 to slider2:', error);
      } finally {
        isUpdating = false;
      }
    });

    // Sync slider2 to slider1
    slider2.on('update', (values, handle) => {
      if (isUpdating) return;
      isUpdating = true;
      
      try {
        slider1.set(values);
        if (callback) callback(values, handle);
      } catch (error) {
        console.warn('Error syncing slider2 to slider1:', error);
      } finally {
        isUpdating = false;
      }
    });
  }

  /**
   * Checks if a filter has active settings
   * @param {Object} filter - Filter instance with getSettings method
   * @param {string} filterType - Type of filter for special handling ('price', 'availability', 'default')
   * @returns {boolean} - True if filter has active settings
   */
  static hasActiveFilterSettings(filter, filterType = 'default') {
    if (!filter || !filter.getSettings) return false;

    const settings = filter.getSettings();
    if (!settings) return false;

    switch (filterType) {
      case 'price':
        if (settings.length > 0) {
          // Check if price filter has actual values (not default range)
          return settings.some(setting => 
            setting && 
            setting !== '0-0' && 
            !setting.includes('min') && 
            !setting.includes('max')
          );
        }
        return false;

      case 'availability':
        // For array-based availability filter, check if it's not empty
        try {
          const parsed = JSON.parse(settings);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return true;
          }
        } catch {
          // Fallback for non-JSON strings
          if (settings.length > 0) {
            return true;
          }
        }
        return false;

      default:
        // For standard filters (color, material, size)
        return Array.isArray(settings) ? settings.length > 0 : Boolean(settings);
    }
  }

  /**
   * Counts active filters across multiple filter instances
   * @param {Array} filterConfigs - Array of {filter, type} objects
   * @returns {number} - Total count of active filters
   */
  static countActiveFilters(filterConfigs) {
    let activeCount = 0;

    filterConfigs.forEach(({ filter, type = 'default' }) => {
      if (this.hasActiveFilterSettings(filter, type)) {
        activeCount++;
      }
    });

    return activeCount;
  }

  /**
   * Updates filter count display element
   * @param {jQuery} filterTrigger - jQuery element containing the filter trigger
   * @param {number} activeFilterCount - Number of active filters
   */
  static updateFilterCountDisplay(filterTrigger, activeFilterCount) {
    if (!filterTrigger || !filterTrigger.length) return;

    let existingCount = filterTrigger.find('.c-facets__filter-count');
    
    if (activeFilterCount > 0) {
      if (existingCount.length === 0) {
        // Create and append the count element
        const countElement = `<span class="c-facets__filter-count" ${activeFilterCount > 9 ? 'data-count="true"' : ''}>(${activeFilterCount})</span>`;
        filterTrigger.append(countElement);
      } else {
        // Update existing count
        existingCount.text(`(${activeFilterCount})`);
        // Update data attribute for styling
        if (activeFilterCount > 9) {
          existingCount.attr('data-count', 'true');
        } else {
          existingCount.removeAttr('data-count');
        }
      }
    } else {
      // Remove count if no active filters
      existingCount.remove();
    }
  }

  /**
   * Checks if any filter from a collection has active settings
   * @param {Object} filters - Object containing filter instances
   * @returns {boolean} - True if any filter is active
   */
  static hasAnyActiveFilters(filters) {
    const filterConfigs = [
      { filter: filters.priceFilter, type: 'price' },
      { filter: filters.colourFilter, type: 'default' },
      { filter: filters.materialFilter, type: 'default' },
      { filter: filters.sizeFilter, type: 'default' },
      { filter: filters.availabilityFilter, type: 'availability' }
    ];

    return filterConfigs.some(({ filter, type }) => 
      this.hasActiveFilterSettings(filter, type)
    );
  }

  /**
   * Debounces a function call
   * @param {Function} func - Function to debounce
   * @param {number} wait - Delay in milliseconds
   * @param {boolean} immediate - Whether to call immediately on first invocation
   * @returns {Function} - Debounced function
   */
  static debounce(func, wait, immediate = false) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  }

  /**
   * Sets up filter change tracking with debounced updates
   * @param {Object} filters - Filters instance
   * @param {Function} updateCallback - Callback to run on filter updates
   * @param {number} delay - Debounce delay in milliseconds
   */
  static setupFilterTracking(filters, updateCallback, delay = 100) {
    if (!filters || !updateCallback) return;

    const debouncedUpdate = this.debounce(updateCallback, delay);

    // Override the filters update method to track changes
    const originalOnFilterUpdate = filters.onFilterUpdate.bind(filters);
    filters.onFilterUpdate = (...args) => {
      const result = originalOnFilterUpdate(...args);
      debouncedUpdate();
      return result;
    };

    return debouncedUpdate;
  }

  /**
   * Count how many products match a filter option
   * Handles tags, availability, and variant options
   */
  static getProductCountForOption(products, filterType, optionValue, options = {}) {
    if (!products || !Array.isArray(products)) return 0;

    let count = 0;

    switch (filterType) {
      case 'tags':
        products.forEach(product => {
          if (product.tags && product.tags.includes(optionValue)) {
            count++;
          }
        });
        break;

      case 'availability':
        products.forEach(product => {
          if (optionValue === true) {
            // Count products that are available (in stock)
            if (product.available === true) {
              count++;
            }
          } else if (optionValue === false) {
            // Count products that are not available (out of stock)
            if (product.available === false) {
              count++;
            }
          }
        });
        break;

      case 'variant-option':
        const { optionNames } = options;
        products.forEach(product => {
          if (product.variants && product.variants.length > 0) {
            // Check if any variant has this option value for this option name
            const hasOption = product.variants.some(variant => {
              if (variant.options && optionNames) {
                return optionNames.some(optionName => {
                  const optionIndex = product.options ? product.options.indexOf(optionName) : -1;
                  if (optionIndex !== -1 && variant.options[optionIndex]) {
                    return variant.options[optionIndex].toLowerCase() === optionValue.toLowerCase();
                  }
                  return false;
                });
              }
              return false;
            });

            if (hasOption) {
              count++;
            }
          }
        });
        break;

      default:
        console.warn(`Unknown filter type: ${filterType}`);
        return 0;
    }

    return count;
  }

  /**
   * Convenience method for tag-based product counting
   * @param {Array} products - Array of product objects
   * @param {string} tagValue - Tag value to match
   * @returns {number} - Count of products with the tag
   */
  static getProductCountForTag(products, tagValue) {
    return this.getProductCountForOption(products, 'tags', tagValue);
  }

  /**
   * Convenience method for availability-based product counting
   * @param {Array} products - Array of product objects
   * @param {boolean} availabilityValue - Availability status to match
   * @returns {number} - Count of products with the availability status
   */
  static getProductCountForAvailability(products, availabilityValue) {
    return this.getProductCountForOption(products, 'availability', availabilityValue);
  }

  /**
   * Convenience method for variant option-based product counting
   * @param {Array} products - Array of product objects
   * @param {string} optionValue - Option value to match
   * @param {Array} optionNames - Array of option names to check against
   * @returns {number} - Count of products with the variant option
   */
  static getProductCountForVariantOption(products, optionValue, optionNames) {
    return this.getProductCountForOption(products, 'variant-option', optionValue, { optionNames });
  }
}

export default FilterUtils;